import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { PageHeader, Card, Modal, Field, Btn, Pill, Empty, Notice, inputCls } from "@/dashboard/ui";
import { Users, Pencil, Trash2, PhoneOff, Search, Plus, CalendarCheck, Upload } from "lucide-react";
import LeadImport from "@/dashboard/LeadImport";
import PhoneInput from "@/components/PhoneInput";

const STATUSES = ["New", "Contacted", "Qualified", "Converted", "Lost"];
const EMPTY_FORM = {
  full_name: "", phone: "", email: "", company: "", photo_url: "",
  status: "New", notes: "", follow_up_date: "",
  call_consent: false, can_call: true, can_sms: false, can_email: true, do_not_call: false,
};
const STATUS_TONE = { New: "outline", Contacted: "grey", Qualified: "black", Converted: "black", Lost: "red" };

function Avatar({ lead }) {
  if (lead.photo_url) {
    return <img src={lead.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" />;
  }
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#111111] text-xs font-bold text-white">
      {lead.full_name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
    </span>
  );
}

function LeadAppointments({ leadId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    setItems(null);
    api.get(`/leads/${leadId}/appointments`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [leadId]);
  if (items === null) return <p className="text-xs text-neutral-400">Loading appointments…</p>;
  return (
    <div className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-4" data-testid="lead-appointments">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500">
        <CalendarCheck size={12} /> Appointments ({items.length})
      </p>
      {items.length === 0 ? (
        <p className="text-xs text-neutral-400">No appointments booked for this lead yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.slice(0, 5).map((a) => (
            <li key={a.id} className="flex items-center justify-between text-xs text-neutral-600" data-testid={`lead-appt-${a.id}`}>
              <span>{a.service_name} · {new Date(a.start).toLocaleString("en-GB", { timeZone: a.timezone || "Europe/London", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {a.staff_name}</span>
              <Pill tone={a.status === "Cancelled" ? "red" : a.status === "Confirmed" ? "black" : "grey"}>{a.status}</Pill>
            </li>
          ))}
        </ul>
      )}
      <Link to={`/dashboard/appointments?tab=list&lead=${leadId}`} data-testid="lead-appointments-link" className="mt-2 inline-block text-xs font-semibold text-[#111111] underline underline-offset-2">
        Open in Appointments →
      </Link>
    </div>
  );
}

export default function Leads() {
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [missingLead, setMissingLead] = useState(false);
  const [highlightId, setHighlightId] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    const { data } = await api.get("/leads", { params });
    setLeads(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  // Deep link: /dashboard/leads?leadId=<id> — verify the lead belongs to this tenant,
  // then show it in the list (filters cleared so any status is visible) and highlight it.
  // The edit form is NOT opened automatically — the pencil icon does that manually.
  useEffect(() => {
    const leadId = searchParams.get("leadId");
    if (!leadId) return;
    (async () => {
      try {
        await api.get(`/leads/${leadId}`);
        setSearch("");
        setStatus("");
        setHighlightId(leadId);
      } catch {
        setMissingLead(true);  // deleted or not owned by this tenant
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // once the list has rendered with the linked lead, scroll it into view
  useEffect(() => {
    if (!highlightId || !leads) return;
    const row = document.querySelector(`[data-testid="lead-row-${highlightId}"]`);
    if (row) row.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [leads, highlightId]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setError("");
    setModal({ mode: "add" });
  };
  const openEdit = (lead) => {
    setForm({ ...EMPTY_FORM, ...lead, email: lead.email || "", follow_up_date: lead.follow_up_date || "" });
    setError("");
    setModal({ mode: "edit", lead });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...form, email: form.email || null, follow_up_date: form.follow_up_date || null };
      if (modal.mode === "add") await api.post("/leads", payload);
      else await api.put(`/leads/${modal.lead.id}`, payload);
      setModal(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete ${lead.full_name}? This cannot be undone.`)) return;
    await api.delete(`/leads/${lead.id}`);
    load();
  };

  const set = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };

  return (
    <div data-testid="leads-page">
      {missingLead && (
        <Notice testid="lead-not-found" title="This CRM lead is no longer available"
          body="The linked lead was deleted or isn't part of your account. You can search the full list below." />
      )}
      <PageHeader title="CRM Leads" sub="Your leads are saved to your account and stay here after refresh. Only you can see them.">
        <Btn variant="ghost" data-testid="import-leads-button" onClick={() => setImportOpen(true)}>
          <Upload size={15} /> Import CSV
        </Btn>
        <Btn data-testid="add-lead-button" onClick={openAdd}>
          <Plus size={15} /> Add Lead
        </Btn>
      </PageHeader>
      {importOpen && <LeadImport onClose={() => setImportOpen(false)} onDone={load} />}

      <div className="mb-4 flex flex-col gap-2.5 sm:flex-row">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="leads-search-input"
            className={`${inputCls} pl-9`}
            placeholder="Search name, company, phone or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          data-testid="leads-status-filter"
          className={`${inputCls} sm:w-44`}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {leads === null ? (
        <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>
      ) : leads.length === 0 ? (
        <Empty
          testid="leads-empty"
          icon={Users}
          title="No leads yet"
          body="Add your first lead — it will be saved to your account and available whenever you return."
        />
      ) : (
        <Card className="overflow-x-auto" data-testid="leads-table">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                <th className="px-5 py-3 font-semibold">Lead</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Follow-up</th>
                <th className="px-4 py-3 font-semibold">Consent</th>
                <th className="px-4 py-3 font-semibold">Added</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} data-testid={`lead-row-${lead.id}`}
                  className={`border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60 ${lead.id === highlightId ? "bg-amber-50/70 ring-1 ring-inset ring-amber-300" : ""}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar lead={lead} />
                      <div>
                        <p className="font-semibold text-[#111111]">
                          {lead.full_name}
                          {lead.id === highlightId && (
                            <span className="ml-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 align-middle text-[10px] font-bold text-amber-700" data-testid="lead-selected-badge">Selected</span>
                          )}
                        </p>
                        {lead.company && <p className="text-xs text-neutral-400">{lead.company}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-neutral-600">{lead.phone}</td>
                  <td className="px-4 py-3.5"><Pill tone={STATUS_TONE[lead.status]}>{lead.status}</Pill></td>
                  <td className="px-4 py-3.5 text-neutral-600">{lead.follow_up_date || "—"}</td>
                  <td className="px-4 py-3.5">
                    {lead.do_not_call ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                        <PhoneOff size={13} /> Do not call
                      </span>
                    ) : lead.call_consent ? (
                      <span className="text-xs font-medium text-neutral-600">Call consented</span>
                    ) : (
                      <span className="text-xs text-neutral-400">Not recorded</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-xs text-neutral-400">{String(lead.created_at).slice(0, 10)}</td>
                  <td className="px-4 py-3.5">
                    <div className="flex justify-end gap-1.5">
                      <button
                        data-testid={`lead-edit-${lead.id}`}
                        aria-label="Edit lead"
                        onClick={() => openEdit(lead)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-[#111111]"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`lead-delete-${lead.id}`}
                        aria-label="Delete lead"
                        onClick={() => remove(lead)}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {modal && (
        <Modal testid="lead-modal" title={modal.mode === "add" ? "Add lead" : `Edit ${modal.lead.full_name}`} onClose={() => setModal(null)}>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name *">
                <input data-testid="lead-name-input" className={inputCls} value={form.full_name} onChange={set("full_name")} required />
              </Field>
              <PhoneInput
                testid="lead-phone-input"
                label="Phone"
                required
                value={form.phone}
                onChange={(v) => set("phone")( { target: { value: v } })}
              />
              <Field label="Email">
                <input data-testid="lead-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} />
              </Field>
              <Field label="Company">
                <input data-testid="lead-company-input" className={inputCls} value={form.company} onChange={set("company")} />
              </Field>
              <Field label="Profile photo URL" hint="Optional — initials are used otherwise">
                <input data-testid="lead-photo-input" className={inputCls} value={form.photo_url} onChange={set("photo_url")} placeholder="https://…" />
              </Field>
              <Field label="Status">
                <select data-testid="lead-status-select" className={inputCls} value={form.status} onChange={set("status")}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Follow-up date">
                <input data-testid="lead-followup-input" type="date" className={inputCls} value={form.follow_up_date} onChange={set("follow_up_date")} />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="lead-notes-input" rows={3} className={inputCls} value={form.notes} onChange={set("notes")} />
            </Field>
            <div className="space-y-2.5 rounded-2xl border border-neutral-200 bg-neutral-50/60 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Consent & preferences</p>
              {[
                ["call_consent", "Customer has consented to receiving calls"],
                ["can_call", "OK to contact by phone"],
                ["can_sms", "OK to contact by SMS"],
                ["can_email", "OK to contact by email"],
                ["do_not_call", "Do not call — opted out (blocks all calling campaigns)"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2.5 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    data-testid={`lead-${key.replace(/_/g, "-")}-checkbox`}
                    checked={form[key]}
                    onChange={set(key)}
                    className="h-4 w-4 rounded accent-[#111111]"
                  />
                  {label}
                </label>
              ))}
            </div>
            {modal.mode === "edit" && <LeadAppointments leadId={modal.lead.id} />}
            {error && (
              <p data-testid="lead-form-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
            )}
            <div className="flex justify-end gap-2.5">
              <Btn type="button" variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn data-testid="lead-save-button" type="submit" disabled={busy}>
                {busy ? "Saving…" : modal.mode === "add" ? "Add Lead" : "Save Changes"}
              </Btn>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
